import { useRef, useState, useContext, useEffect } from "react";
import styles from "./styles/PosSummary.module.css";
import { useTranslation } from "react-i18next";
import OutputWindow from "../../../components/ui/output_window/OutputWindow";
import Spinner from "../../../components/ui/spinner/Spinner";
import Button from "../../../components/ui/button/Button";
import InputText from "../../../components/ui/input_text/InputText";
import DataContext from "../../../context/DataContext";
import { capitalizeFirstLetter, encryptor } from "../../../utils/utils";
import { axiosSW, axiosSWLogIn } from "../../../api/axiosDefaults";
import AuthContext from "../../../context/AuthContext";
import POSDataContext from "../../../context/POSDataContext";
import InputTextArea from "../../../components/ui/input_text_area/InputTextArea";
import PosSummaryDetails from "./PosSummaryDetails";

/**
 * The summary modal
 * Show the info of the transaction to be canceled
 * Authinticate the user for the transaction cancellation operation
 */
const PosSummaryModal = ({
    setShowModal, // {Function} - Function to set the visibility of the summary modal
    setShowInfoModal, // {Function} - Function to set the visibility of the info modal (confirmation and user feedback)
    setInfoMessage, // {Function} - Function to set the info message to show
    currentTransaction, // {Object} - The current transaction data
}) => {
    const { t } = useTranslation();

    const { currentCustom, connection } = useContext(DataContext);
    const {
        handleErrorPos,
        setIsLoadingPos,
        fetchSummary,
        isProcessingPos,
        userLogout,
    } = useContext(POSDataContext);
    const { user, login } = useContext(AuthContext);
    const userNameRef = useRef(null);
    const [userData, setUserData] = useState({
        userName: "",
        userPassword: "",
        comment: "",
    });

    const [hoverFlag, setHoverFlag] = useState(false);
    const [inProcess, setInProcess] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoging, setIsLoging] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [formButtonDisable, setFormButtonDisable] = useState(true);
    const [confirmButtonDisable, setConfirmButtonDisable] = useState(true);

    // Check ready for viewing

    // Set focus to the name input
    useEffect(() => {
        userNameRef.current && userNameRef.current.focus();
    }, []);

    // Set form button disable state for authentication
    useEffect(() => {
        setFormButtonDisable(!(userData?.userName && userData?.userPassword));
    }, [userData]);

    // Set form button state for the transaction info view
    useEffect(() => {
        setConfirmButtonDisable(!userData?.comment);
    }, [userData]);

    /**
     * Reset the form data
     */
    const handleReset = () => {
        if (inProcess) return;
        setSuccess(false);
        setIsAdmin(false);
        setUserData((prev) => ({
            ...prev,
            userName: "",
            userPassword: "",
            generalTerms: false,
        }));
        setHoverFlag(false);
    };

    /**
     * Cancel button handler
     * Used when clicking the cancel button in the authintication view
     * Will reset the input for the user login form and hide the summary modal
     */
    const handleCancelButton = () => {
        if (!inProcess) {
            handleReset();
            setShowModal(false);
        }
    };

    /**
     * Confirmation button handler
     * Used in the info view when clicking confirm cancellation button
     * It will set the is loging state to true which will show the login dialog
     */
    const handleConfirmButton = () => {
        if (!confirmButtonDisable && !inProcess) {
            setIsLoging(true);
            setHoverFlag(false);
        }
    };

    /**
     * The cancel operation handler
     * submit the data and return the statusOk value
     * @returns {Boolean} - The state of the cancelation operation
     */
    const handleCancelOperation = async () => {
        const payLoad = {
            uniqueIdentifier: connection?.uniqueIdentifier,
            cashierSessionID: user?.cashierSessionID,

            cartID: user?.cartID,
            referenceNo: currentTransaction?.referenceNo,
            refundAmount: currentTransaction?.totalAmount,
            refundReason: userData?.comment,
        };

        try {
            const { data } = await axiosSW.post(
                "Payment/pos/ProcessRefund",
                payLoad,
            );

            return data?.statusOk;
        } catch (error) {
            if (error?.request?.status === 404) {
                setIsLoadingPos(false);
                userLogout();
            } else {
                setIsLoadingPos(false);

                handleErrorPos(error);
            }
        }
    };

    /**
     * Change handler
     */
    const handleChange = (event) => {
        setUserData((prevUserData) => ({
            ...prevUserData,
            [event.target.name]:
                event.target.type === "checkbox"
                    ? event.target.checked
                    : event.target.value,
        }));
    };

    /**
     * The submit handler
     * Will submit the user authintication and if successfull, it will excute the handleCancelOperation function.
     * And if the responce from the handleCancelOperation function is true (successful)
     * 1- The IsAdmin is set to true
     * 2- The summary modal is hidden
     * 3- Show the info modal with the feedback message
     * 4- Fetch the summary
     *
     * If the respond is false
     * 1- The IsAdmin is set to false
     * 2- Show an error message, for the error message the info modal is not needed,
     * the message will appear in the window of the login form,
     * this view appears after the isLoging is set to false.
     *
     * The combination isAdmin, success, inProcess, and isLoging values are controlling how the view will change
     * from showing the transaction info to show the login form and the error feedback
     *
     * @param {Event} event - The event object
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formButtonDisable) {
            setInProcess(true);

            const payload = {
                uniqueIdentifier: connection?.uniqueIdentifier,
                intKey: connection?.intKey,
                username: userData?.userName,
                password: encryptor(userData?.userPassword, connection?.intKey),
            };
            try {
                const { data } = await axiosSWLogIn.post(
                    "Authentication/pos/ValidateCashierCredentials",
                    payload,
                );

                setSuccess(true); // success means that the server sent a respond
                if (data?.role === "Supervisor") {
                    const confirmation = await handleCancelOperation();
                    // Hide the modal in case of success and show the info modal
                    // with the success message and OK or confim button
                    if (confirmation) {
                        handleReset();
                        setIsAdmin(true);
                        setShowModal(false);
                        setInfoMessage(
                            capitalizeFirstLetter(
                                t(`transaction deleted successfully!`),
                            ),
                        );
                        setShowInfoModal(true);
                        setIsLoging(false); // isLoging means that the authintication view is shown
                        fetchSummary();
                    }
                } else {
                    // If the authintication faild show the error message
                    setErrorMessage(
                        capitalizeFirstLetter(t(`insufficient authorization!`)),
                        // capitalizeFirstLetter(t(data?.statusMessage))
                    );
                    // isAdmin, inPrecess, and success will hide the login form and show error message form
                    setIsAdmin(false);
                    setInProcess(false);
                    setSuccess(true);
                }
                setInProcess(false);
            } catch (error) {
                // If respond status is 401 do the same as not authenticated
                if (error?.request?.status === 401) {
                    setErrorMessage(
                        // capitalizeFirstLetter(t(`insufficient authorization!`))
                        capitalizeFirstLetter(
                            t(error?.response?.data?.statusMessage),
                        ),
                    );
                    setIsAdmin(false);
                    setInProcess(false);
                    setSuccess(true);
                } else {
                    console.log(error);
                    setInProcess(false);
                    handleErrorPos(error);
                }
            }
        }
    };

    return (
        <section
            className={`${styles.modal_back} ${
                !isAdmin && isLoging ? styles.modal_center : ""
            }   flex-container`}
        >
            <div
                className={`${styles.main_form_container} ${
                    success || inProcess ? styles.window : ""
                } ${!isLoging ? styles.modal_details : ""} 
                      fade-in v-flex-container`}
            >
                {/* Show error view if success and not admin
                    This view appears when the authintication fails */}
                {(success && !isAdmin) || inProcess ? (
                    <>
                        <div
                            className={`${styles.output_window_container}  v-flex-container`}
                        >
                            <OutputWindow
                                content={
                                    inProcess ? (
                                        <div
                                            className={`${styles.spinner_container}  flex-container`}
                                        >
                                            <Spinner small={true} />
                                        </div>
                                    ) : (
                                        <div
                                            className={`${styles.result_text_container} v-flex-container`}
                                        >
                                            <h1>{t(errorMessage)}</h1>
                                        </div>
                                    )
                                }
                                color={
                                    inProcess
                                        ? currentCustom?.color?.current
                                        : currentCustom?.color?.error
                                }
                            />
                        </div>
                        {/* Dont show the button while in process */}

                        <div
                            className={`${styles.result_buttons_container}  ${
                                inProcess ? "hidden" : ""
                            } flex-container`}
                        >
                            <Button
                                onClickFuction={handleReset}
                                body={t(`go back`)}
                                type={"button"}
                                disabled={inProcess}
                                disableHTML={inProcess}
                            />
                        </div>
                    </>
                ) : !isLoging && !isAdmin ? (
                    // show the transaction info view if user is not loging and user is not admin
                    // this view should appear when the user clicks on the  X button in a transaction tab
                    <>
                        <div
                            className={`${
                                !inProcess ? styles.output_window_container : ""
                            }  v-flex-container`}
                        >
                            <OutputWindow
                                alignTop={true}
                                minHeight={"21rem"}
                                content={
                                    inProcess || isProcessingPos ? (
                                        <div
                                            className={`${styles.spinner_container}  flex-container`}
                                        >
                                            <Spinner />
                                        </div>
                                    ) : (
                                        // Show the details of the transaction
                                        <PosSummaryDetails
                                            currentTransaction={
                                                currentTransaction
                                            }
                                        />
                                    )
                                }
                                color={currentCustom?.color?.current}
                            />
                        </div>
                        <div
                            className={`${styles.comment_container} flex-container`}
                        >
                            <InputTextArea
                                label={`${t("reason")}*`}
                                inputId={"comment"}
                                inputValue={userData?.comment}
                                handleChangeFunction={handleChange}
                                required={hoverFlag && confirmButtonDisable}
                                rows={3}
                                cols={50}
                                placeholder={t(`reason is required!`)}
                                fullWidth={true}
                            />
                        </div>
                        {/* Dont show the buttons while in process */}

                        <div
                            className={`${styles.result_buttons_container} flex-container`}
                        >
                            <div
                                className={`${styles.confiramtion_button_container}`}
                            >
                                <Button
                                    size={"adapt"}
                                    color={"green"}
                                    onClickFuction={handleConfirmButton}
                                    body={t(`confirm cancellation`)}
                                    type={"button"}
                                    disabled={
                                        confirmButtonDisable ||
                                        isProcessingPos ||
                                        inProcess
                                    }
                                    disableHTML={isProcessingPos}
                                    setHoverFlag={setHoverFlag}
                                />
                            </div>
                            <Button
                                onClickFuction={handleCancelButton}
                                color={"red"}
                                body={t(`cancel`)}
                                type={"button"}
                                disabled={isProcessingPos || inProcess}
                                disableHTML={isProcessingPos}
                            />
                        </div>
                    </>
                ) : (
                    // If the user is loging and not authenticated show the login form
                    // This should occer when the user clicks the confirm cancelation button
                    !isAdmin &&
                    isLoging && (
                        <>
                            <div
                                className={`${styles.form_header_container} flex-container`}
                            >
                                <h1 className={`${styles.form_header}`}>
                                    {t(`authorization`)}
                                </h1>
                            </div>
                            <form
                                className={`${styles.form_container} v-flex-container`}
                                onSubmit={handleSubmit}
                                noValidate={true}
                            >
                                <div
                                    className={`${styles.form_inputs_container}`}
                                >
                                    <InputText
                                        ref={userNameRef}
                                        letterSpacing={".2rem"}
                                        placeholder={t(`required field`)}
                                        label={t(`user name`)}
                                        inputId="userName"
                                        inputValue={userData?.userName}
                                        handleChangeFunction={handleChange}
                                        required={
                                            hoverFlag && !userData?.userName
                                        }
                                    />
                                    <InputText
                                        type={"password"}
                                        letterSpacing={".2rem"}
                                        placeholder={t(`required field`)}
                                        label={t(`password`)}
                                        inputId="userPassword"
                                        inputValue={userData?.userPassword}
                                        handleChangeFunction={handleChange}
                                        required={
                                            hoverFlag && !userData?.userPassword
                                        }
                                    />
                                </div>
                                <div
                                    className={`${styles.form_buttons_container} flex-container`}
                                >
                                    <div className={`flex-container`}>
                                        <Button
                                            body={t(`submit`)}
                                            type={"submit"}
                                            disabled={
                                                formButtonDisable || inProcess
                                            }
                                            disableHTML={inProcess}
                                            setHoverFlag={setHoverFlag}
                                        />
                                    </div>
                                    <div className={`flex-container`}>
                                        <Button
                                            body={t(`cancel`)}
                                            type={"button"}
                                            backgroundColor={"red"}
                                            onClickFuction={() =>
                                                setShowModal(false)
                                            }
                                        />
                                    </div>
                                </div>
                            </form>
                        </>
                    )
                )}
            </div>
        </section>
    );
};

export default PosSummaryModal;
